import { useEffect, useState } from "react";

const BRAND = "#6366f1";
const BG = "#05070a";
const CARD_BG = "#0d1117";
const BORDER = "rgba(99,102,241,0.15)";
const MUTED = "#94a3b8";
const TEXT = "#f8fafc";

const style = {
  root: {
    background: BG,
    minHeight: "100vh",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    color: TEXT,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    padding: "24px 16px",
  },
  inner: {
    width: "100%",
    maxWidth: 400,
    display: "flex",
    flexDirection: "column" as const,
    gap: 20,
  },
  logo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 4,
  },
  card: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    padding: "20px 20px",
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    color: MUTED,
    marginBottom: 4,
  },
  value: {
    fontSize: 15,
    fontWeight: 600,
    color: TEXT,
  },
  stageBadge: (stage: string) => ({
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    background: stage === "dossier_ready" || stage === "outcome"
      ? "rgba(74,222,128,0.12)"
      : stage === "not_proceeding" || stage === "expired"
      ? "rgba(248,113,113,0.12)"
      : "rgba(99,102,241,0.15)",
    color: stage === "dossier_ready" || stage === "outcome"
      ? "#4ade80"
      : stage === "not_proceeding" || stage === "expired"
      ? "#f87171"
      : "#a5b4fc",
    border: `1px solid ${
      stage === "dossier_ready" || stage === "outcome"
        ? "rgba(74,222,128,0.25)"
        : stage === "not_proceeding" || stage === "expired"
        ? "rgba(248,113,113,0.25)"
        : "rgba(99,102,241,0.3)"
    }`,
  }),
  primaryBtn: {
    display: "block",
    width: "100%",
    padding: "11px 20px",
    background: BRAND,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    textAlign: "center" as const,
    textDecoration: "none",
  },
  disabledBtn: {
    display: "block",
    width: "100%",
    padding: "11px 20px",
    background: "rgba(99,102,241,0.35)",
    color: "rgba(255,255,255,0.5)",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "not-allowed",
    textAlign: "center" as const,
  },
  muted: {
    fontSize: 12,
    color: MUTED,
    lineHeight: 1.5,
  },
  errorText: {
    fontSize: 12,
    color: "#f87171",
    marginTop: 8,
  },
  successText: {
    fontSize: 12,
    color: "#4ade80",
    marginTop: 8,
  },
  divider: {
    borderTop: `1px solid ${BORDER}`,
    margin: "12px 0",
  },
};

interface CandidateData {
  status: "found" | "not_found" | "not_imported" | "error";
  candidateName?: string;
  roleTitle?: string;
  pipelineStage?: string;
  pipelineStageLabel?: string;
  assessmentSent?: boolean;
  dossierReady?: boolean;
  dossierUrl?: string | null;
  workspaceId?: number;
  applicationId?: number;
  dispatchToken?: string;
  message?: string;
  roleConfigured?: boolean;
  roleId?: number;
  vexisRoleSetupUrl?: string;
}

type SendState =
  | { type: "idle" }
  | { type: "sending" }
  | { type: "sent" }
  | { type: "expired" }
  | { type: "already_used" }
  | { type: "error"; message: string };

type LinkState = "loading" | "invalid" | "ok";

export default function JobadderCandidate() {
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const applicationId = params.get("applicationId") || "";
  const workspaceToken = params.get("workspaceToken") || "";
  const exp = params.get("_exp") || "";
  const hash = params.get("_h") || "";

  const [linkState, setLinkState] = useState<LinkState>("loading");
  const [data, setData] = useState<CandidateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendState, setSendState] = useState<SendState>({ type: "idle" });

  useEffect(() => {
    console.log("[VEXIS SIDEDRAWER] URL:", typeof window !== "undefined" ? window.location.href : "ssr");
    console.log("[VEXIS SIDEDRAWER] Search:", typeof window !== "undefined" ? window.location.search : "ssr");
    console.log("[VEXIS SIDEDRAWER] applicationId:", applicationId);
    console.log("[VEXIS SIDEDRAWER] workspaceToken:", workspaceToken ? "present" : "missing");
    if (!applicationId || !workspaceToken) {
      setData({ status: "not_found", message: "Missing required parameters (applicationId, workspaceToken)" });
      setLinkState("ok");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const r = await fetch(
          `https://vexistalentagency.com/api/jobadder/candidate?applicationId=${encodeURIComponent(applicationId)}&workspaceToken=${encodeURIComponent(workspaceToken)}&_exp=${encodeURIComponent(exp)}&_h=${encodeURIComponent(hash)}`
        );
        if (r.status === 401) {
          setLinkState("invalid");
          return;
        }
        setLinkState("ok");
        const d = await r.json() as CandidateData;
        setData(d);
      } catch {
        setLinkState("ok");
        setData({ status: "error", message: "Failed to load candidate information." });
      } finally {
        setLoading(false);
      }
    })();
  }, [applicationId, workspaceToken]);

  async function handleSendAssessment() {
    if (!data?.dispatchToken) return;
    setSendState({ type: "sending" });

    try {
      const r = await fetch("/api/jobadder/dispatch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: data.dispatchToken }),
      });
      const json = await r.json().catch(() => ({}));

      if (r.ok && (json as any).success) {
        setSendState({ type: "sent" });
        setData((prev) =>
          prev
            ? {
                ...prev,
                assessmentSent: true,
                pipelineStage: "link_sent",
                pipelineStageLabel: "Assessment Sent",
                dispatchToken: undefined,
              }
            : prev,
        );
        return;
      }

      const errCode = (json as any).error as string | undefined;
      if (errCode === "token_expired") {
        setSendState({ type: "expired" });
      } else if (errCode === "token_already_used") {
        setSendState({ type: "already_used" });
        // Refresh the panel to show current state
        fetch(`https://vexistalentagency.com/api/jobadder/candidate?applicationId=${encodeURIComponent(applicationId)}&workspaceToken=${encodeURIComponent(workspaceToken)}`)
          .then((r2) => r2.json())
          .then((d: CandidateData) => setData(d))
          .catch(() => {});
      } else {
        const errorMsg =
          (json as any).message ??
          (json as any).error ??
          "Something went wrong. Please try again.";
        setSendState({ type: "error", message: errorMsg });
      }
    } catch {
      setSendState({ type: "error", message: "Network error — please check your connection." });
    }
  }

  const canSend = !!data?.dispatchToken && sendState.type === "idle";
  const isSending = sendState.type === "sending";

  return (
    <div style={style.root}>
      <div style={style.inner}>
        <div style={style.logo}>
          <img src="/vexis-logo.png" alt="Vexis" style={{ height: 32, width: "auto" }} />
        </div>

        {loading && (
          <div style={{ ...style.card, textAlign: "center" }}>
            <p style={{ color: MUTED, fontSize: 14, margin: 0 }}>Loading candidate…</p>
          </div>
        )}

        {/* ── Invalid / expired link (401) — no retry ── */}
        {!loading && linkState === "invalid" && (
          <div style={style.card} data-testid="panel-invalid-link">
            <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, margin: 0 }}>
              This link is invalid or has expired. Please reopen from JobAdder.
            </p>
          </div>
        )}

        {/* ── Not found / error ── */}
        {!loading && linkState === "ok" && (data?.status === "not_found" || data?.status === "error") && (
          <div style={style.card} data-testid="panel-not-found">
            <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, margin: 0 }}>
              {data?.status === "error"
                ? (data.message ?? "An error occurred loading this candidate.")
                : "This candidate has not been connected to a Vexis workspace. Please ensure your JobAdder integration is active."}
            </p>
          </div>
        )}

        {/* ── Token expired — no retry ── */}
        {!loading && linkState === "ok" && sendState.type === "expired" && (
          <div style={style.card} data-testid="panel-token-expired">
            <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, margin: 0 }}>
              This page has expired. Please close and reopen from JobAdder.
            </p>
          </div>
        )}

        {/* ── Assessment not yet sent ── */}
        {!loading &&
          linkState === "ok" &&
          data?.status === "found" &&
          !data.assessmentSent &&
          sendState.type !== "expired" &&
          sendState.type !== "sent" && (
            <div style={style.card} data-testid="panel-not-sent">
              <div style={{ marginBottom: 16 }}>
                <p style={style.label}>Candidate</p>
                <p style={style.value} data-testid="text-candidate-name">{data.candidateName}</p>
              </div>
              <div style={style.divider} />
              <div style={{ marginBottom: 16 }}>
                <p style={style.label}>Role</p>
                <p style={style.value} data-testid="text-role-title">{data.roleTitle ?? "—"}</p>
              </div>
              <div style={style.divider} />
              <div style={{ marginBottom: 16 }}>
                <p style={style.label}>Pipeline Stage</p>
                <span style={style.stageBadge(data.pipelineStage ?? "")} data-testid="badge-pipeline-stage">
                  {data.pipelineStageLabel ?? data.pipelineStage}
                </span>
              </div>
              <div style={style.divider} />

              {sendState.type === "already_used" ? (
                <p style={{ ...style.muted, margin: 0 }} data-testid="text-already-used">
                  Assessment already sent.
                </p>
              ) : data.roleConfigured === false ? (
                <div data-testid="panel-role-setup-required">
                  <div style={{
                    background: "#fff8e1",
                    border: "1px solid #f9a825",
                    borderRadius: 8,
                    padding: "12px 14px",
                    marginBottom: 14,
                  }}>
                    <p style={{ margin: "0 0 6px 0", fontWeight: 600, color: "#b45309", fontSize: 13 }}>
                      Role Setup Required
                    </p>
                    <p style={{ margin: "0 0 10px 0", color: "#78350f", fontSize: 12, lineHeight: 1.5 }}>
                      This role is using default interview questions. Configure custom questions before sending an assessment to <strong>{data.candidateName}</strong>.
                    </p>
                    <a
                      href={data.vexisRoleSetupUrl ?? `https://vexistalentagency.com/agent/dashboard?editQuestions=${data.roleId}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "inline-block",
                        padding: "7px 14px",
                        background: "#b45309",
                        color: "#fff",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                      data-testid="link-role-setup">
                      Configure Role Questions
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <p style={{ ...style.muted, marginBottom: 14 }}>
                    Send <strong style={{ color: TEXT }}>{data.candidateName}</strong> a Vexis video assessment for <strong style={{ color: TEXT }}>{data.roleTitle}</strong>?
                  </p>
                  <button
                    style={canSend ? style.primaryBtn : style.disabledBtn}
                    onClick={canSend ? handleSendAssessment : undefined}
                    disabled={!canSend || isSending}
                    data-testid="button-send-assessment">
                    {isSending ? "Sending…" : "Send Assessment"}
                  </button>
                  {sendState.type === "error" && (
                    <p style={style.errorText} data-testid="text-send-error">
                      {sendState.message}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

        {/* ── Assessment in progress or complete ── */}
        {!loading && linkState === "ok" && data?.status === "found" && (data.assessmentSent || sendState.type === "sent") && sendState.type !== "expired" && (
          <div style={style.card} data-testid="panel-in-progress">
            <div style={{ marginBottom: 16 }}>
              <p style={style.label}>Candidate</p>
              <p style={style.value} data-testid="text-candidate-name-active">{data.candidateName}</p>
            </div>
            <div style={style.divider} />
            <div style={{ marginBottom: data.dossierReady ? 16 : 0 }}>
              <p style={style.label}>Pipeline Stage</p>
              <span
                style={style.stageBadge(data.pipelineStage ?? "link_sent")}
                data-testid="badge-pipeline-stage-active">
                {sendState.type === "sent" && !data.dossierReady
                  ? "Assessment Sent"
                  : (data.pipelineStageLabel ?? data.pipelineStage)}
              </span>
            </div>
            {sendState.type === "sent" && (
              <p style={style.successText} data-testid="text-sent-confirmation">
                Assessment link sent successfully.
              </p>
            )}
            {data.dossierReady && data.dossierUrl && (
              <>
                <div style={style.divider} />
                <a
                  href={data.dossierUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={style.primaryBtn}
                  data-testid="link-view-dossier">
                  View Dossier
                </a>
              </>
            )}
            {data.dossierReady && !data.dossierUrl && (
              <>
                <div style={style.divider} />
                <p style={{ ...style.muted, margin: 0 }}>
                  Dossier is being prepared. Check back shortly or visit the Vexis dashboard.
                </p>
              </>
            )}
          </div>
        )}

        <p style={{ ...style.muted, textAlign: "center", marginTop: 4 }}>
          Powered by{" "}
          <a
            href="https://vexistalentagency.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#a5b4fc", textDecoration: "none" }}>
            Vexis Talent Agency
          </a>
        </p>
      </div>
    </div>
  );
}
